import { createId } from '@paralleldrive/cuid2'
import {
  ApplicationError,
  type Transaction,
  defineMutator,
  defineMutators,
} from '@rocicorp/zero'
import slugLib from 'slug'
import { z } from 'zod'

import { MAX_REPOS_PER_SESSION } from '@trevize/api/modules/implementations/implementations.schema'
import {
  secretKeySchema,
  secretValueSchema,
} from '@trevize/api/modules/secrets/secrets.schema'
import {
  ENVIRONMENTS_FEATURE_FLAG,
  hasWorkspaceFeature,
  isReservedWorkspaceSlug,
  isSandboxImageId,
} from '@trevize/config'

import { schema } from '../../zero-schema.gen.ts'
import { computeNextRunAt } from './automations-schedule.ts'
import './zero-default-types.ts'
import { buildOptimisticSessionMessage } from './session-message-row.ts'
import { isWorkspaceManagerRole } from './workspace-access.ts'
import type { ZeroQueryContext } from './zero-default-types.ts'

const DEFAULT_WORKSPACE_TAGS = [
  { id: 'developer', name: 'Developer', color: 'purple' },
  { id: 'designer', name: 'Designer', color: 'green' },
  { id: 'partner', name: 'Partner', color: 'blue' },
  { id: 'prospect', name: 'Prospect', color: 'gray' },
] as const

const workspaceUpdateSchema = z.object({
  workspaceId: z.string().min(1),
  name: z
    .string()
    .min(1, 'Please enter your workspace name.')
    .min(2, 'Please choose a name with at least 3 characters.')
    .max(50, 'The name should be no longer than 50 characters.')
    .describe('Name'),
  slug: z
    .string()
    .min(1, 'Please enter your workspace URL.')
    .min(2, 'Please choose an URL with at least 3 characters.')
    .max(50, 'The URL should be no longer than 50 characters.')
    .describe('URL')
    .regex(
      /^[a-z0-9-]+$/,
      'The URL should only contain lowercase letters, numbers, and dashes.',
    ),
  logo: z.string().url().optional().describe('Logo'),
})

const workspaceGitSettingsSchema = z.object({
  workspaceId: z.string().min(1),
  gitCommitAttributionMode: z.enum(['tervezo', 'user']),
})

const workspaceFeatureSettingsSchema = z.object({
  workspaceId: z.string().min(1),
  knowledgeEnabled: z.boolean(),
})

const workspaceCiSettingsSchema = z.object({
  workspaceId: z.string().min(1),
  autoFixCiEnabled: z.boolean(),
})

const workspaceScreenshotSharingSettingsSchema = z.object({
  workspaceId: z.string().min(1),
  githubScreenshotSharingEnabled: z.boolean(),
})

const workspaceLinearSettingsSchema = z.object({
  workspaceId: z.string().min(1),
  linearTaskRuntime: z.enum(['implementation', 'environment']),
})

const workspaceSandboxImageSettingsSchema = z.object({
  workspaceId: z.string().min(1),
  defaultSandboxImageId: z
    .string()
    .min(1)
    .refine(isSandboxImageId, 'Unknown sandbox image'),
})

const notificationChannelsSchema = z.object({
  email: z.boolean().optional(),
  desktop: z.boolean().optional(),
})

const notificationTopicsSchema = z.object({
  contacts_new_lead: z.boolean().optional(),
  contacts_account_upgraded: z.boolean().optional(),
  inbox_assigned_to_me: z.boolean().optional(),
  inbox_mentioned: z.boolean().optional(),
})

const newslettersSchema = z.object({
  product_updates: z.boolean().optional(),
  important_updates: z.boolean().optional(),
})

const workspaceMemberSettingsSchema = z.object({
  workspaceId: z.string().min(1),
  channels: notificationChannelsSchema.optional(),
  topics: notificationTopicsSchema.optional(),
  newsletters: newslettersSchema.optional(),
  dismissedOnboardingCards: z.array(z.string()).optional(),
})

const dismissOnboardingCardSchema = z.object({
  workspaceId: z.string().min(1),
  cardId: z.string().min(1),
})

const updateWorkspaceMemberRolesSchema = z.object({
  workspaceId: z.string().min(1),
  userId: z.string().min(1),
  roles: z.array(z.string().min(1)).min(1),
})

const inviteWorkspaceMembersSchema = z.object({
  workspaceId: z.string().min(1),
  emails: z.array(z.string().email()).min(1),
  role: z.string().optional(),
})

const removeWorkspaceMemberSchema = z.object({
  workspaceId: z.string().min(1),
  id: z.string().min(1),
})

const acceptWorkspaceInvitationSchema = z.object({
  token: z.string().min(1),
})

const createTagSchema = z.object({
  workspaceId: z.string().min(1),
  name: z.string().min(1),
  color: z.string().optional().nullable(),
})

const updateTagSchema = z.object({
  workspaceId: z.string().min(1),
  id: z.string().min(1),
  name: z.string().min(1),
  color: z.string().optional().nullable(),
})

const deleteTagSchema = z.object({
  workspaceId: z.string().min(1),
  id: z.string().min(1),
})

const markNotificationAsReadSchema = z.object({
  workspaceId: z.string().min(1),
  notificationId: z.string().min(1),
})

const upsertIntegrationSchema = z.object({
  workspaceId: z.string().min(1),
  integrationId: z.string().min(1),
  accessToken: z.string().min(1).optional(),
})

const disconnectIntegrationSchema = z.object({
  workspaceId: z.string().min(1),
  integrationId: z.string().min(1),
})

const briefResourceMetadataSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('github_repository'),
    owner: z.string(),
    name: z.string(),
    fullName: z.string(),
    url: z.string(),
  }),
  z.object({
    type: z.literal('linear_project'),
    name: z.string(),
    url: z.string(),
    teamKey: z.string().optional(),
  }),
  z.object({
    type: z.literal('linear_issue'),
    identifier: z.string(),
    title: z.string(),
    url: z.string(),
  }),
])

const scheduleInputSchema = z.object({
  type: z.enum(['hourly', 'daily', 'weekly', 'monthly']),
  hour: z.number().int().min(0).max(23).optional(),
  minute: z.number().int().min(0).max(59).optional(),
  dayOfMonth: z.number().int().min(1).max(31).optional(),
  // Day of week for weekly schedules (Sunday=0 … Saturday=6, matching Date.getDay()).
  dayOfWeek: z.number().int().min(0).max(6).optional(),
  timezone: z.string().max(100).default('UTC'),
})

const automationRepositorySchema = z.object({
  repositoryName: z.string().min(1).max(500),
  installationId: z.number().int(),
  baseBranch: z.string().max(255).optional(),
})

const automationRepositoriesSchema = z
  .array(automationRepositorySchema)
  .min(1, 'At least one repository is required')
  .max(
    MAX_REPOS_PER_SESSION,
    `Maximum ${MAX_REPOS_PER_SESSION} repositories per automation`,
  )

const automationTriggerSchema = z.enum(['schedule', 'friction'])

/** Reports a friction item needs before a friction-triggered automation runs. */
const frictionMinOccurrencesSchema = z.number().int().min(1).max(1000)

const createAutomationSchema = z
  .object({
    id: z.string().min(1),
    workspaceId: z.string().min(1),
    name: z.string().min(1).max(255),
    description: z.string().optional(),
    // Friction automations take their instructions from the friction report.
    instructions: z.string(),
    repositories: automationRepositoriesSchema,
    model: z.string().min(1).max(255).optional(),
    blueprintId: z.string().min(1),
    triggerType: automationTriggerSchema.default('schedule'),
    frictionMinOccurrences: frictionMinOccurrencesSchema.optional(),
    schedules: z.array(scheduleInputSchema).default([]),
    env: z.record(z.string(), z.string()).optional(),
    secretEnv: z.record(z.string(), z.string()).optional(),
  })
  .refine(
    (args) =>
      args.triggerType === 'schedule'
        ? args.schedules.length > 0 && args.instructions.trim().length > 0
        : args.frictionMinOccurrences !== undefined,
    {
      message:
        'A scheduled automation needs instructions and a schedule; a friction automation needs a report count.',
    },
  )

const updateAutomationSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(255).optional(),
  description: z.string().nullable().optional(),
  instructions: z.string().optional(),
  repositories: automationRepositoriesSchema.optional(),
  model: z.string().min(1).max(255).optional(),
  blueprintId: z.string().min(1).optional(),
  enabled: z.boolean().optional(),
  triggerType: automationTriggerSchema.optional(),
  frictionMinOccurrences: frictionMinOccurrencesSchema.nullable().optional(),
  env: z.record(z.string(), z.string()).nullable().optional(),
  secretEnv: z.record(z.string(), z.string()).nullable().optional(),
})

const deleteAutomationSchema = z.object({
  id: z.string().min(1),
})

const toggleAutomationSchema = z.object({
  id: z.string().min(1),
  enabled: z.boolean(),
})

const updateSchedulesSchema = z.object({
  automationId: z.string().min(1),
  // Empty for friction-triggered automations, which run without a schedule.
  schedules: z.array(scheduleInputSchema),
})

const triggerAutomationSchema = z.object({
  id: z.string().min(1),
})

const deleteSecretSchema = z.object({
  workspaceId: z.string().min(1),
  id: z.string().min(1),
})

export const createSecretSchema = z.object({
  workspaceId: z.string().min(1),
  key: secretKeySchema,
  value: secretValueSchema,
  description: z.string().max(1000).optional(),
})

export const updateSecretSchema = z.object({
  workspaceId: z.string().min(1),
  id: z.string().min(1),
  value: secretValueSchema.optional(),
  description: z.string().max(1000).nullish(),
})

const archiveImplementationSchema = z.object({ id: z.string().min(1) })
export const prOperationSchema = z.object({
  id: z.string().min(1),
  runRepoId: z.string().min(1),
})
const unarchiveImplementationSchema = z.object({ id: z.string().min(1) })
const cancelImplementationSchema = z.object({ id: z.string().min(1) })
const pauseImplementationSchema = z.object({ id: z.string().min(1) })
const resumeImplementationSchema = z.object({ id: z.string().min(1) })

/**
 * Resolve an `action_required` harness-readiness suspension for the current
 * step visit. Without `model` the same target is retried (Continue after a
 * settings repair); with `model` the selection is recorded as an audited
 * current-visit override and the visit resumes on the replacement. Scope is
 * strictly the suspended visit — the immutable blueprint snapshot and later
 * steps are untouched.
 */
export const resolveStepRemediationSchema = z.object({
  id: z.string().min(1),
  model: z.string().min(1).optional(),
})
const deleteImplementationSchema = z.object({ id: z.string().min(1) })

/**
 * Sentinel `blueprintId` meaning "let the server pick the best blueprint".
 *
 * The composer sends this instead of a concrete blueprint id when the user
 * chooses the **Auto** option. It is a valid non-empty string, so the schema's
 * `blueprintId: z.string().min(1)` still accepts it unchanged — the server
 * mutator resolves it to a real blueprint id before starting the run, keeping
 * `packages/api` and the DBOS workflow unaware of the sentinel.
 */
export const AUTO_BLUEPRINT_ID = 'auto'

export const startAdHocImplementationSchema = z.object({
  id: z.string().min(1),
  workspaceId: z.string().min(1),
  prompt: z.string().min(1),
  blueprintId: z.string().min(1),
  // Multi-repo input (preferred)
  repos: z
    .array(
      z.object({
        repositoryName: z.string().min(1),
        installationId: z.number().int(),
        baseBranch: z.string().optional(),
      }),
    )
    .min(1)
    .max(5)
    .optional(),
  // Legacy single-repo input (deprecated — use repos[])
  repositoryName: z.string().min(1).optional(),
  installationId: z.number().int().optional(),
  baseBranch: z.string().optional(),
  // Optional: present only when the user explicitly picked a **Default model**
  // in the composer, which stores it as the task's explicit default. Omitted
  // means the task inherits — each new run resolves the member's current
  // configured default (platform default as fallback).
  model: z.string().min(1).optional(),
  stepConfigOverrides: z
    .record(z.string(), z.object({ awaitUserApproval: z.boolean().optional() }))
    .optional(),
})

export const setFrictionStatusSchema = z
  .object({
    id: z.string().min(1),
    workspaceId: z.string().min(1),
    status: z.enum(['open', 'resolved', 'dismissed']),
    reason: z.string().trim().max(500).optional(),
  })
  .refine((args) => args.status !== 'dismissed' || Boolean(args.reason), {
    message: 'A reason is required to dismiss.',
    path: ['reason'],
  })

export const fixFrictionSchema = z.object({
  id: z.string().min(1),
  workspaceId: z.string().min(1),
  /** Client-generated task id so the inbox can link to it right away. */
  implementationId: z.string().min(1),
})

export const createFrictionIssueSchema = z.object({
  id: z.string().min(1),
  workspaceId: z.string().min(1),
})

export const createSessionSchema = z.object({
  id: z.string().min(1),
  workspaceId: z.string().min(1),
  repos: z
    .array(
      z.object({
        repositoryName: z.string().min(1),
        installationId: z.number().int(),
        baseBranch: z.string().optional(),
      }),
    )
    .min(1)
    .max(5),
  model: z.string().min(1).optional(),
  title: z.string().min(1).optional(),
})

const sessionMentionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('user'), userId: z.string().min(1) }),
  z.object({ type: z.literal('agent') }),
])

export const postSessionMessageSchema = z.object({
  /**
   * Generated by the composer so the optimistic client row and the
   * authoritative server row share an id — Zero then replaces the optimistic
   * message on sync instead of rendering it twice.
   */
  id: z.string().min(1),
  workspaceId: z.string().min(1),
  runId: z.string().min(1),
  text: z.string().min(1),
  mentions: z.array(sessionMentionSchema).optional(),
})

export const touchSessionSchema = z.object({
  workspaceId: z.string().min(1),
  runId: z.string().min(1),
})

export const openSessionPrSchema = z.object({
  workspaceId: z.string().min(1),
  runId: z.string().min(1),
})

const removeGithubInstallationSchema = z.object({
  workspaceId: z.string().min(1),
  installationId: z.number().int(),
})

const refreshGithubRepositoriesSchema = z.object({
  workspaceId: z.string().min(1),
})

export const sendEventSchema = z.object({
  implementationId: z.string().min(1),
  type: z.enum(['user_message', 'stop', 'approve']),
  payload: z
    .object({
      message: z.string().optional(),
      stepId: z.string().optional(),
    })
    .optional(),
})

export const chatImplementationSchema = z.object({
  /**
   * Generated by the composer so the optimistic client row and the
   * authoritative server row share an id — Zero then replaces the optimistic
   * message on sync instead of rendering it twice.
   */
  id: z.string().min(1),
  workspaceId: z.string().min(1),
  implementationId: z.string().min(1),
  /**
   * The run the composer was viewing. The server rejects the mutation when
   * this is no longer the implementation's current run (e.g. after Restore),
   * which rolls the optimistic row back instead of leaving a ghost message.
   */
  runId: z.string().min(1),
  text: z.string().min(1),
  /** Optional paused-visit model choice applied with this follow-up. */
  model: z.string().min(1).optional(),
})

export const createUICommentSchema = z.object({
  workspaceId: z.string().min(1),
  implementationId: z.string().min(1),
  previewSessionId: z.string().min(1).optional(),
  body: z.string().min(1),
  sourceFile: z.string().optional(),
  sourceLine: z.number().int().positive().optional(),
  sourceColumn: z.number().int().positive().optional(),
  selectorPath: z.string().optional(),
  htmlSnapshot: z.string().max(50_000).optional(),
  styleSnapshot: z.string().max(50_000).optional(),
  stackTrace: z.string().max(10_000).optional(),
})

const submitFeedbackSchema = z.object({
  type: z.enum(['problem', 'question', 'feedback']),
  comment: z.string().min(1),
  url: z.string().optional(),
  workspaceSlug: z.string().optional(),
  userAgent: z.string().optional(),
  viewport: z.string().optional(),
})

const updateUserProfileSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  image: z.string().optional(),
})

const createBriefSchema = z.object({
  id: z.string().min(1),
  workspaceId: z.string().min(1),
  title: z.string().optional(),
  content: z.string().optional(),
})

const updateBriefSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1).optional(),
  content: z.string().optional(),
})

const setBriefStatusSchema = z.object({
  id: z.string().min(1),
  status: z.enum(['draft', 'approved', 'archived']),
})

const deleteBriefSchema = z.object({
  id: z.string().min(1),
})

const setBriefResourcesSchema = z.object({
  briefId: z.string().min(1),
  resources: z.array(
    z.object({
      type: z.enum(['github_repository', 'linear_project', 'linear_issue']),
      externalId: z.string().min(1),
      metadata: briefResourceMetadataSchema,
    }),
  ),
})

const createWorkspaceInputSchema = z.object({
  name: z.string(),
  slug: z.string(),
  id: z.string().optional(),
  logoUrl: z.string().url().optional(),
})

type WorkspaceMemberAccess = {
  userId: string
  workspaceId: string
  role: string
}

function assertSignedIn(ctx: ZeroQueryContext | undefined) {
  if (!ctx?.user.id) {
    throw new ApplicationError('You must be signed in.')
  }
  return ctx
}

function assertServerSideEffects(ctx: ZeroQueryContext) {
  const sideEffects = ctx.workspaceMembers
  if (sideEffects === undefined) {
    throw new ApplicationError('Workspace member side effects unavailable.')
  }
  return sideEffects
}

async function assertActiveWorkspaceMember(
  tx: Transaction<typeof schema>,
  ctx: ZeroQueryContext | undefined,
  workspaceId: string,
): Promise<WorkspaceMemberAccess> {
  const signedIn = assertSignedIn(ctx)

  if (tx.location !== 'server') {
    return {
      userId: signedIn.user.id,
      workspaceId,
      role: '',
    }
  }

  const rows = Array.from(
    await tx.dbTransaction.query(
      'select role, status::text as status from workspace_members where workspace_id = $1 and user_id = $2 limit 1',
      [workspaceId, signedIn.user.id],
    ),
  )

  const row = rows[0] ?? null
  const role = getString(row?.['role'])
  const status = getString(row?.['status'])

  if (role === null || status !== 'active') {
    throw new ApplicationError('Not an active member of this workspace.')
  }

  return {
    userId: signedIn.user.id,
    workspaceId,
    role,
  }
}

async function assertCanManageWorkspaceMembers(
  tx: Transaction<typeof schema>,
  ctx: ZeroQueryContext | undefined,
  workspaceId: string,
) {
  const access = await assertActiveWorkspaceMember(tx, ctx, workspaceId)

  if (tx.location === 'server' && !isWorkspaceManagerRole(access.role)) {
    throw new ApplicationError(
      'You do not have permission to manage workspace members.',
    )
  }

  return access
}

async function assertCanManageWorkspaceSettings(
  tx: Transaction<typeof schema>,
  ctx: ZeroQueryContext | undefined,
  workspaceId: string,
) {
  const access = await assertActiveWorkspaceMember(tx, ctx, workspaceId)

  if (tx.location === 'server' && !isWorkspaceManagerRole(access.role)) {
    throw new ApplicationError(
      'You do not have permission to manage workspace settings.',
    )
  }

  return access
}

async function assertTargetMemberInWorkspace(
  tx: Transaction<typeof schema>,
  workspaceId: string,
  userId: string,
) {
  if (tx.location !== 'server') {
    return
  }

  const rows = Array.from(
    await tx.dbTransaction.query(
      'select user_id from workspace_members where workspace_id = $1 and user_id = $2 limit 1',
      [workspaceId, userId],
    ),
  )

  if (getString(rows[0]?.['user_id']) === null) {
    throw new ApplicationError('Workspace member not found.')
  }
}

async function assertCanAccessBrief(
  tx: Transaction<typeof schema>,
  ctx: ZeroQueryContext | undefined,
  briefId: string,
) {
  const signedIn = assertSignedIn(ctx)

  if (tx.location !== 'server') {
    return { userId: signedIn.user.id, workspaceId: '' }
  }

  const rows = Array.from(
    await tx.dbTransaction.query(
      'select b.workspace_id from briefs b inner join workspace_members wm on wm.workspace_id = b.workspace_id where b.id = $1 and wm.user_id = $2 and wm.status = $3 limit 1',
      [briefId, signedIn.user.id, 'active'],
    ),
  )
  const workspaceId = getString(rows[0]?.['workspace_id'])

  if (workspaceId === null) {
    throw new ApplicationError('Brief not found.')
  }

  return { userId: signedIn.user.id, workspaceId }
}

type AutomationRepositoryInput = z.infer<typeof automationRepositorySchema>

/**
 * Replace an automation's repositories. The first one also fills the legacy
 * single-repo columns that older code still reads.
 */
async function replaceAutomationRepositories(
  tx: Transaction<typeof schema>,
  automationId: string,
  repositories: readonly AutomationRepositoryInput[],
) {
  if (tx.location === 'server') {
    const existing = Array.from(
      await tx.dbTransaction.query(
        'select id from automation_repositories where automation_id = $1',
        [automationId],
      ),
    )
    for (const row of existing) {
      const id = getString(row['id'])
      if (id !== null) {
        await tx.mutate.automationRepositories.delete({ id })
      }
    }
  }
  for (const [position, repository] of repositories.entries()) {
    await tx.mutate.automationRepositories.insert({
      id: createId(),
      automationId,
      repositoryName: repository.repositoryName,
      installationId: repository.installationId,
      baseBranch: repository.baseBranch ?? null,
      position,
    })
  }
}

async function assertCanAccessAutomation(
  tx: Transaction<typeof schema>,
  ctx: ZeroQueryContext | undefined,
  automationId: string,
) {
  const signedIn = assertSignedIn(ctx)

  if (tx.location !== 'server') {
    return { userId: signedIn.user.id, workspaceId: '' }
  }

  const rows = Array.from(
    await tx.dbTransaction.query(
      'select a.workspace_id from automations a inner join workspace_members wm on wm.workspace_id = a.workspace_id where a.id = $1 and wm.user_id = $2 and wm.status = $3 limit 1',
      [automationId, signedIn.user.id, 'active'],
    ),
  )
  const workspaceId = getString(rows[0]?.['workspace_id'])

  if (workspaceId === null) {
    throw new ApplicationError('Automation not found.')
  }

  return { userId: signedIn.user.id, workspaceId }
}

function assertInvitationBelongsToSignedInUser(args: {
  invitedUserId: string | null
  invitedEmail: string | null
  ctx: ZeroQueryContext
}) {
  if (args.invitedUserId !== null && args.invitedUserId !== args.ctx.user.id) {
    throw new ApplicationError('The provided token does not match the user')
  }

  if (
    args.invitedUserId === null &&
    args.invitedEmail !== args.ctx.user.email
  ) {
    throw new ApplicationError('The provided token does not match the user')
  }
}

async function assertSlugGloballyAvailable(
  tx: Transaction<typeof schema>,
  slugValue: string,
) {
  if (tx.location !== 'server') {
    return
  }

  const rows = Array.from(
    await tx.dbTransaction.query(
      'select id from workspaces where slug = $1 limit 1',
      [slugValue],
    ),
  )

  if (rows.length > 0) {
    throw new ApplicationError('Workspace url is already taken')
  }
}

function assertSlugNotReserved(slugValue: string) {
  if (isReservedWorkspaceSlug(slugValue)) {
    throw new ApplicationError('This workspace URL is reserved.')
  }
}

function getString(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

function getBoolean(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null
}

function getTimestamp(value: unknown): number | null {
  if (typeof value === 'number') {
    return value
  }
  if (value instanceof Date) {
    return value.getTime()
  }
  return null
}

function getCount(value: unknown): number {
  if (typeof value === 'number') {
    return value
  }
  if (typeof value === 'string') {
    const count = Number.parseInt(value, 10)
    return Number.isNaN(count) ? 0 : count
  }
  return 0
}

/**
 * Custom mutators run optimistically on the client and authoritatively on the
 * server — see `apps/web/src/api/zero-mutate.ts`. Use `ctx` to enforce permissions.
 */
export const mutators = defineMutators({
  userProfiles: {
    setLocale: defineMutator(
      z.object({
        locale: z.string().min(2).max(10),
      }),
      async ({ tx, ctx, args }) => {
        if (!ctx) {
          return
        }
        await tx.mutate.userProfiles.upsert({
          id: ctx.user.id,
          locale: args.locale,
        })
      },
    ),
    /**
     * Persist the user's preferred default model, used when an issue is started
     * through Linear (a server-side flow with no interactive picker). Passing a
     * model id sets the preference; passing `null` clears it, reverting the user
     * to the platform default. The id is stored verbatim and is **not** validated
     * against the live model catalog here — mirroring `useLastSelectedModel`, an
     * id that looks unknown may just be one whose config has not loaded yet, and
     * the authoritative `pickModel` on the server already falls back to the
     * platform default for any stored id that is no longer valid. Length caps at
     * the `user_profiles.default_model` column width (varchar 100).
     */
    setDefaultModel: defineMutator(
      z.object({
        defaultModel: z.string().min(1).max(100).nullable(),
      }),
      async ({ tx, ctx, args }) => {
        if (!ctx) {
          return
        }
        await tx.mutate.userProfiles.upsert({
          id: ctx.user.id,
          defaultModel: args.defaultModel,
        })
      },
    ),
  },
  workspaces: {
    /**
     * Same side effects as former tRPC `workspaces.create` (workspace row, member,
     * billing account, default subscription when configured, default tags).
     * Client: no-op until server confirms; global slug check runs on server only.
     */
    create: defineMutator(
      createWorkspaceInputSchema,
      async ({ tx, ctx, args }) => {
        if (tx.location === 'client') {
          return
        }

        if (args === undefined) {
          throw new ApplicationError('Invalid workspace create payload.')
        }

        const signedIn = assertSignedIn(ctx)

        const normalizedSlug = slugLib(args.slug)
        assertSlugNotReserved(normalizedSlug)
        await assertSlugGloballyAvailable(tx, normalizedSlug)

        const workspaceId = args.id ?? createId()
        await tx.mutate.workspaces.insert({
          id: workspaceId,
          ownerId: signedIn.user.id,
          slug: normalizedSlug,
          name: args.name,
        })

        await tx.mutate.workspaceMembers.insert({
          userId: signedIn.user.id,
          workspaceId,
          role: 'admin',
          status: 'active',
        })

        await tx.mutate.billingAccounts.insert({
          id: workspaceId,
        })

        const planId = import.meta.env.DEFAULT_PLAN_ID
        if (typeof planId === 'string' && planId.length > 0) {
          const now = Date.now()
          const periodEnd = now + 100 * 365 * 24 * 60 * 60 * 1000

          await tx.mutate.billingSubscriptions.insert({
            id: createId(),
            accountId: workspaceId,
            planId,
            status: 'active',
            quantity: 1,
            startedAt: now,
            currentPeriodStart: now,
            currentPeriodEnd: periodEnd,
          })
        }

        if (args.logoUrl) {
          try {
            // Routed through `ctx.workspaces` (wired up server-side in
            // zero-mutate.ts, see zero-workspace-side-effects.ts) rather than
            // importing server code directly: the workflow client and its
            // deps aren't browser-safe, and even a dynamic `import()` here
            // would get code-split into the client bundle by Vite.
            // `ctx.workspaces` is `undefined` on the client, so this is a
            // no-op there. Enqueueing is fast; the logo fetch+upload and the
            // `workspaces.logo` update run on the workflow worker after this
            // transaction commits (see store-workspace-logo.workflow.ts).
            await ctx?.workspaces?.enqueueStoreLogoFromUrl({
              workspaceId,
              iconUrl: args.logoUrl,
            })
          } catch (error) {
            // A logo failure must never fail workspace creation.
            console.error('Failed to enqueue workspace logo storage:', error)
          }
        }

        for (const tag of DEFAULT_WORKSPACE_TAGS) {
          await tx.mutate.tags.insert({
            id: tag.id,
            workspaceId,
            name: tag.name,
            color: tag.color,
          })
        }
      },
    ),

    /**
     * Former `workspaces.update` (admin): name, slug, optional logo URL.
     */
    update: defineMutator(workspaceUpdateSchema, async ({ tx, ctx, args }) => {
      await assertCanManageWorkspaceMembers(tx, ctx, args.workspaceId)

      if (tx.location === 'server') {
        const currentRows = Array.from(
          await tx.dbTransaction.query(
            'select slug from workspaces where id = $1 limit 1',
            [args.workspaceId],
          ),
        )
        const currentSlug = getString(currentRows[0]?.['slug'])

        if (currentSlug !== args.slug) {
          assertSlugNotReserved(args.slug)
        }
      }

      await tx.mutate.workspaces.update({
        id: args.workspaceId,
        name: args.name,
        slug: args.slug,
        ...(args.logo !== undefined ? { logo: args.logo } : {}),
      })
    }),

    updateGitSettings: defineMutator(
      workspaceGitSettingsSchema,
      async ({ tx, ctx, args }) => {
        await assertCanManageWorkspaceMembers(tx, ctx, args.workspaceId)

        await tx.mutate.workspaces.update({
          id: args.workspaceId,
          gitCommitAttributionMode: args.gitCommitAttributionMode,
        })
      },
    ),

    updateFeatureSettings: defineMutator(
      workspaceFeatureSettingsSchema,
      async ({ tx, ctx, args }) => {
        await assertCanManageWorkspaceMembers(tx, ctx, args.workspaceId)

        await tx.mutate.workspaces.update({
          id: args.workspaceId,
          knowledgeEnabled: args.knowledgeEnabled,
        })
      },
    ),

    updateCiAutofixSettings: defineMutator(
      workspaceCiSettingsSchema,
      async ({ tx, ctx, args }) => {
        await assertCanManageWorkspaceMembers(tx, ctx, args.workspaceId)

        await tx.mutate.workspaces.update({
          id: args.workspaceId,
          autoFixCiEnabled: args.autoFixCiEnabled,
        })
      },
    ),

    updateScreenshotSharingSettings: defineMutator(
      workspaceScreenshotSharingSettingsSchema,
      async ({ tx, ctx, args }) => {
        await assertCanManageWorkspaceSettings(tx, ctx, args.workspaceId)

        await tx.mutate.workspaces.update({
          id: args.workspaceId,
          githubScreenshotSharingEnabled: args.githubScreenshotSharingEnabled,
        })
      },
    ),

    updateLinearSettings: defineMutator(
      workspaceLinearSettingsSchema,
      async ({ tx, ctx, args }) => {
        await assertCanManageWorkspaceSettings(tx, ctx, args.workspaceId)

        if (
          args.linearTaskRuntime === 'environment' &&
          tx.location === 'server'
        ) {
          const rows = Array.from(
            await tx.dbTransaction.query(
              'select feature_flags from workspaces where id = $1 limit 1',
              [args.workspaceId],
            ),
          )
          const value = rows[0]?.['feature_flags']
          const featureFlags =
            Array.isArray(value) &&
            value.every((item) => typeof item === 'string')
              ? value
              : []
          if (
            !hasWorkspaceFeature({ featureFlags }, ENVIRONMENTS_FEATURE_FLAG)
          ) {
            throw new ApplicationError(
              'Trevize Code environments are not enabled for this workspace.',
            )
          }
        }

        await tx.mutate.workspaces.update({
          id: args.workspaceId,
          linearTaskRuntime: args.linearTaskRuntime,
        })
      },
    ),

    updateSandboxImageSettings: defineMutator(
      workspaceSandboxImageSettingsSchema,
      async ({ tx, ctx, args }) => {
        await assertCanManageWorkspaceSettings(tx, ctx, args.workspaceId)

        await tx.mutate.workspaces.update({
          id: args.workspaceId,
          defaultSandboxImageId: args.defaultSandboxImageId,
        })
      },
    ),
  },
  workspaceMembers: {
    invite: defineMutator(
      inviteWorkspaceMembersSchema,
      async ({ tx, ctx, args }) => {
        if (tx.location === 'client') {
          return
        }

        const access = await assertCanManageWorkspaceMembers(
          tx,
          ctx,
          args.workspaceId,
        )
        const signedIn = assertSignedIn(ctx)
        const sideEffects = assertServerSideEffects(signedIn)

        const workspaceRows = Array.from(
          await tx.dbTransaction.query(
            'select w.id, w.name, bs.plan_id from workspaces w left join billing_subscriptions bs on bs.account_id = w.id where w.id = $1 limit 1',
            [args.workspaceId],
          ),
        )
        const workspaceRow = workspaceRows[0]
        const workspaceId = getString(workspaceRow?.['id'])
        const workspaceName = getString(workspaceRow?.['name'])
        if (workspaceId === null || workspaceName === null) {
          throw new ApplicationError(
            `Could not find workspace with id ${args.workspaceId}`,
          )
        }

        const memberCountRows = Array.from(
          await tx.dbTransaction.query(
            'select count(*) as count from workspace_members where workspace_id = $1',
            [workspaceId],
          ),
        )
        const memberCount =
          getCount(memberCountRows[0]?.['count']) + args.emails.length

        const planId =
          getString(workspaceRow?.['plan_id']) ??
          import.meta.env.DEFAULT_PLAN_ID

        if (planId) {
          await sideEffects.throwIfUserLimitReached({
            planId,
            quantity: memberCount,
          })
        }

        const existingUserRows = Array.from(
          await tx.dbTransaction.query(
            'select id, email from auth_users where email = any($1)',
            [args.emails],
          ),
        )
        const existingUsers = existingUserRows.flatMap((row) => {
          const id = getString(row['id'])
          const email = getString(row['email'])
          return id !== null && email !== null ? [{ id, email }] : []
        })

        const existingUserIds = existingUsers.map((user) => user.id)
        const memberRows = existingUserIds.length
          ? Array.from(
              await tx.dbTransaction.query(
                'select user_id from workspace_members where workspace_id = $1 and user_id = any($2)',
                [workspaceId, existingUserIds],
              ),
            )
          : []
        const memberIds = memberRows.flatMap((row) => {
          const userId = getString(row['user_id'])
          return userId === null ? [] : [userId]
        })

        const newUsers = existingUsers.filter(
          (user) => !memberIds.includes(user.id),
        )
        const newEmails = args.emails.filter(
          (email) => !existingUsers.some((user) => user.email === email),
        )
        const invitees = [
          ...newUsers.map((user) => ({ email: user.email, userId: user.id })),
          ...newEmails.map((email) => ({ email, userId: undefined })),
        ]

        const role = args.role ?? 'member'
        const sentInvitations: Array<{ id: string; email: string }> = []

        for (const invitee of invitees) {
          const existingInvitationRows = Array.from(
            await tx.dbTransaction.query(
              'select id from workspace_invitations where workspace_id = $1 and email = $2 limit 1',
              [workspaceId, invitee.email],
            ),
          )
          const existingInvitationId = getString(
            existingInvitationRows[0]?.['id'],
          )
          const invitationId = existingInvitationId ?? createId()

          if (existingInvitationId === null) {
            await tx.mutate.workspaceInvitations.insert({
              id: invitationId,
              workspaceId,
              email: invitee.email,
              role,
              invitedBy: access.userId,
              userId: invitee.userId,
            })
          } else {
            await tx.mutate.workspaceInvitations.update({
              id: invitationId,
              workspaceId,
              role,
              invitedBy: access.userId,
              updatedAt: Date.now(),
            })
          }

          sentInvitations.push({ id: invitationId, email: invitee.email })
        }

        if (sentInvitations.length === 0) {
          return
        }

        await sideEffects.sendInvitations({
          workspaceName,
          invitedByName: signedIn.user.name,
          invitations: sentInvitations,
        })
      },
    ),
    acceptInvitation: defineMutator(
      acceptWorkspaceInvitationSchema,
      async ({ tx, ctx, args }) => {
        if (tx.location === 'client') {
          return
        }

        const signedIn = assertSignedIn(ctx)

        const rows = Array.from(
          await tx.dbTransaction.query(
            'select id, workspace_id, user_id, email, role, accepted, expires_at from workspace_invitations where id = $1 limit 1',
            [args.token],
          ),
        )
        const invitation = rows[0]
        const invitationId = getString(invitation?.['id'])
        const workspaceId = getString(invitation?.['workspace_id'])
        const invitedUserId = getString(invitation?.['user_id'])
        const invitedEmail = getString(invitation?.['email'])
        const role = getString(invitation?.['role']) ?? 'member'
        const accepted = getBoolean(invitation?.['accepted'])
        const expiresAt = getTimestamp(invitation?.['expires_at'])

        if (invitationId === null || workspaceId === null) {
          throw new ApplicationError('Invitation not found')
        }
        if (accepted === true) {
          throw new ApplicationError(
            'The provided token has already been accepted',
          )
        }
        if (expiresAt !== null && expiresAt < Date.now()) {
          throw new ApplicationError('The provided token is expired')
        }
        assertInvitationBelongsToSignedInUser({
          invitedUserId,
          invitedEmail,
          ctx: signedIn,
        })

        await tx.mutate.workspaceMembers.upsert({
          userId: signedIn.user.id,
          workspaceId,
          role,
          status: 'active',
        })

        await tx.mutate.workspaceInvitations.delete({
          id: invitationId,
          workspaceId,
        })

        await signedIn.workspaceMembers?.syncSubscriptionQuantity(workspaceId)
      },
    ),
    removeMember: defineMutator(
      removeWorkspaceMemberSchema,
      async ({ tx, ctx, args }) => {
        if (tx.location === 'client') {
          return
        }

        const access = await assertCanManageWorkspaceMembers(
          tx,
          ctx,
          args.workspaceId,
        )

        const memberRows = Array.from(
          await tx.dbTransaction.query(
            'select user_id from workspace_members where workspace_id = $1 and user_id = $2 limit 1',
            [args.workspaceId, args.id],
          ),
        )
        const memberUserId = getString(memberRows[0]?.['user_id'])

        if (memberUserId === null) {
          await tx.mutate.workspaceInvitations.delete({
            id: args.id,
            workspaceId: args.workspaceId,
          })
          return
        }

        await tx.mutate.workspaceMembers.update({
          userId: args.id,
          workspaceId: args.workspaceId,
          status: 'suspended',
        })

        await ctx?.workspaceMembers?.syncSubscriptionQuantity(
          access.workspaceId,
        )
      },
    ),
    updateRoles: defineMutator(
      updateWorkspaceMemberRolesSchema,
      async ({ tx, ctx, args }) => {
        await assertCanManageWorkspaceMembers(tx, ctx, args.workspaceId)
        await assertTargetMemberInWorkspace(tx, args.workspaceId, args.userId)

        await tx.mutate.workspaceMembers.update({
          userId: args.userId,
          workspaceId: args.workspaceId,
          role: args.roles[0],
        })
      },
    ),
    updateNotificationSettings: defineMutator(
      workspaceMemberSettingsSchema,
      async ({ tx, ctx, args }) => {
        const access = await assertActiveWorkspaceMember(
          tx,
          ctx,
          args.workspaceId,
        )

        await tx.mutate.workspaceMemberSettings.upsert({
          userId: access.userId,
          workspaceId: args.workspaceId,
          channels: args.channels,
          topics: args.topics,
          newsletters: args.newsletters,
          dismissedOnboardingCards: args.dismissedOnboardingCards,
        })
      },
    ),
    dismissOnboardingCard: defineMutator(
      dismissOnboardingCardSchema,
      async ({ tx, ctx, args }) => {
        const access = await assertActiveWorkspaceMember(
          tx,
          ctx,
          args.workspaceId,
        )

        let current: readonly string[] = []

        if (tx.location === 'server') {
          const rows = Array.from(
            await tx.dbTransaction.query(
              'select dismissed_onboarding_cards from workspace_member_settings where user_id = $1 and workspace_id = $2 limit 1',
              [access.userId, args.workspaceId],
            ),
          )
          const value = rows[0]?.['dismissed_onboarding_cards']
          if (
            Array.isArray(value) &&
            value.every((item) => typeof item === 'string')
          ) {
            current = value
          }
        }

        if (current.includes(args.cardId)) {
          return
        }

        await tx.mutate.workspaceMemberSettings.upsert({
          userId: access.userId,
          workspaceId: args.workspaceId,
          dismissedOnboardingCards: [...current, args.cardId],
        })
      },
    ),
  },
  tags: {
    create: defineMutator(createTagSchema, async ({ tx, ctx, args }) => {
      await assertActiveWorkspaceMember(tx, ctx, args.workspaceId)

      await tx.mutate.tags.insert({
        id: slugLib(args.name).toLowerCase(),
        workspaceId: args.workspaceId,
        name: args.name,
        color: args.color,
      })
    }),
    update: defineMutator(updateTagSchema, async ({ tx, ctx, args }) => {
      await assertActiveWorkspaceMember(tx, ctx, args.workspaceId)

      await tx.mutate.tags.update({
        id: args.id,
        workspaceId: args.workspaceId,
        name: args.name,
        color: args.color,
      })
    }),
    delete: defineMutator(deleteTagSchema, async ({ tx, ctx, args }) => {
      await assertActiveWorkspaceMember(tx, ctx, args.workspaceId)

      await tx.mutate.tags.delete({
        id: args.id,
        workspaceId: args.workspaceId,
      })
    }),
  },
  notifications: {
    markAsRead: defineMutator(
      markNotificationAsReadSchema,
      async ({ tx, ctx, args }) => {
        const access = await assertActiveWorkspaceMember(
          tx,
          ctx,
          args.workspaceId,
        )

        if (tx.location === 'server') {
          const rows = Array.from(
            await tx.dbTransaction.query(
              'select id from notification where workspace_id = $1 and id = $2 and target_type = $3 and target_id = $4 limit 1',
              [args.workspaceId, args.notificationId, 'user', access.userId],
            ),
          )

          if (getString(rows[0]?.['id']) === null) {
            throw new ApplicationError('Notification not found.')
          }
        }

        await tx.mutate.notifications.update({
          workspaceId: args.workspaceId,
          id: args.notificationId,
          readAt: Date.now(),
          readById: access.userId,
        })
      },
    ),
  },
  integrations: {
    upsert: defineMutator(
      upsertIntegrationSchema,
      async ({ tx, ctx, args }) => {
        if (tx.location === 'client') {
          return
        }

        await assertActiveWorkspaceMember(tx, ctx, args.workspaceId)

        const rows = Array.from(
          await tx.dbTransaction.query(
            'select id from workspace_integrations where workspace_id = $1 and integration_id = $2 limit 1',
            [args.workspaceId, args.integrationId],
          ),
        )
        const existingId = getString(rows[0]?.['id'])
        const id = existingId ?? createId()

        if (existingId === null) {
          await tx.mutate.workspaceIntegrations.insert({
            id,
            workspaceId: args.workspaceId,
            integrationId: args.integrationId,
            status: 'active',
          })
        } else {
          await tx.mutate.workspaceIntegrations.update({
            id,
            workspaceId: args.workspaceId,
            status: 'active',
          })
        }

        if (args.accessToken !== undefined) {
          await tx.dbTransaction.query(
            'update workspace_integrations set access_token = $1 where workspace_id = $2 and id = $3',
            [args.accessToken, args.workspaceId, id],
          )
        }
      },
    ),
    disconnect: defineMutator(
      disconnectIntegrationSchema,
      async ({ tx, ctx, args }) => {
        if (tx.location === 'client') {
          return
        }

        await assertActiveWorkspaceMember(tx, ctx, args.workspaceId)

        const rows = Array.from(
          await tx.dbTransaction.query(
            'select id from workspace_integrations where workspace_id = $1 and integration_id = $2 limit 1',
            [args.workspaceId, args.integrationId],
          ),
        )
        const id = getString(rows[0]?.['id'])
        if (id === null) {
          return
        }

        await tx.mutate.workspaceIntegrations.update({
          id,
          workspaceId: args.workspaceId,
          status: 'inactive',
        })

        await tx.dbTransaction.query(
          'update workspace_integrations set access_token = null, refresh_token = null where workspace_id = $1 and id = $2',
          [args.workspaceId, id],
        )
      },
    ),
  },
  secrets: {
    delete: defineMutator(deleteSecretSchema, async ({ tx, ctx, args }) => {
      await assertCanManageWorkspaceMembers(tx, ctx, args.workspaceId)
      await tx.mutate.workspaceRuntimeSecrets.delete({ id: args.id })
    }),
    /**
     * Server-only — encryption uses the server's KMS. Client stub does
     * nothing optimistically; see {@link serverMutators} for the real
     * implementation.
     */
    create: defineMutator(createSecretSchema, async () => {}),
    /** Server-only — see {@link serverMutators}. */
    update: defineMutator(updateSecretSchema, async () => {}),
  },
  sessions: {
    /**
     * Server-only — creates a standalone collaborative run (+ repos + initial
     * participant). See {@link serverMutators}.
     */
    create: defineMutator(createSessionSchema, async () => {}),
    /**
     * Writes the user's message as a `run_messages` row so it renders in the
     * channel the instant it's sent, then the server override records the
     * participant, bumps activity, and wakes the agent when the message is
     * agent-directed. See {@link serverMutators}.
     */
    postMessage: defineMutator(
      postSessionMessageSchema,
      async ({ tx, ctx, args }) => {
        const access = await assertActiveWorkspaceMember(
          tx,
          ctx,
          args.workspaceId,
        )

        // Only ever runs client-side: `serverMutators` overrides this key with
        // the authoritative write (participant roster, activity, agent wake-up).
        await tx.mutate.runMessages.insert(
          buildOptimisticSessionMessage({
            id: args.id,
            workspaceId: access.workspaceId,
            runId: args.runId,
            userId: access.userId,
            text: args.text,
            ...(args.mentions ? { mentions: args.mentions } : {}),
            // `runMessages` is ordered by `createdAt`; without a value the
            // optimistic row would sort ahead of the existing history.
            createdAt: Date.now(),
          }),
        )
      },
    ),
    /**
     * Server-only — records activity (roster `last_active_at` + global
     * `last_seen_at`). See {@link serverMutators}.
     */
    touch: defineMutator(touchSessionSchema, async () => {}),
    /**
     * Server-only — cancels the active turn (Stop). See {@link serverMutators}.
     */
    stop: defineMutator(touchSessionSchema, async () => {}),
    /**
     * Server-only — pushes changed repos and opens/updates PRs for the session.
     * See {@link serverMutators}.
     */
    openPr: defineMutator(openSessionPrSchema, async () => {}),
    /**
     * Server-only — flips `archivedAt` so the session is hidden from the
     * default list. See {@link serverMutators}.
     */
    archive: defineMutator(touchSessionSchema, async () => {}),
    /** Server-only — clears `archivedAt`. See {@link serverMutators}. */
    unarchive: defineMutator(touchSessionSchema, async () => {}),
  },
  implementations: {
    /** Server-only — completes a reviewable blueprint workflow run before archiving it. */
    archive: defineMutator(archiveImplementationSchema, async () => {}),
    /** Server-only — completes a reviewable blueprint workflow run without archiving it. */
    finish: defineMutator(archiveImplementationSchema, async () => {}),
    /** Server-only — see {@link serverMutators}. */
    unarchive: defineMutator(unarchiveImplementationSchema, async () => {}),
    /**
     * Server-only — cancels via DBOS. See {@link serverMutators}.
     */
    cancel: defineMutator(cancelImplementationSchema, async () => {}),
    /** Server-only — requests a resumable Blueprint workflow pause. */
    pause: defineMutator(pauseImplementationSchema, async () => {}),
    /** Server-only — removes a Blueprint workflow user hold and resumes when ready. */
    resume: defineMutator(resumeImplementationSchema, async () => {}),
    /** Server-only — cancels the active run before deleting the implementation. */
    delete: defineMutator(deleteImplementationSchema, async () => {}),
    /** Server-only — starts a DBOS-backed autonomous implementation. */
    startAdHoc: defineMutator(startAdHocImplementationSchema, async () => {}),
    /**
     * Server-only — appends a timeline event and routes feedback to the
     * active Claude session. See {@link serverMutators}.
     */
    createUIComment: defineMutator(createUICommentSchema, async () => {}),
    /**
     * Writes the user's chat message as a `run_messages` row so it renders in
     * the timeline the instant it's sent. The server override validates the
     * run is still current, persists the authoritative row under the same id,
     * and routes the message into the Blueprint workflow. See
     * {@link serverMutators}.
     */
    chat: defineMutator(chatImplementationSchema, async ({ tx, ctx, args }) => {
      const access = await assertActiveWorkspaceMember(
        tx,
        ctx,
        args.workspaceId,
      )
      await tx.mutate.runMessages.insert(
        buildOptimisticSessionMessage({
          id: args.id,
          workspaceId: access.workspaceId,
          runId: args.runId,
          userId: access.userId,
          text: args.text,
          // `runMessages` is ordered by `createdAt`; without a value the
          // optimistic row would sort ahead of the existing history.
          createdAt: Date.now(),
        }),
      )
    }),
    /** Server-only — opens a GitHub PR via Octokit. See {@link serverMutators}. */
    createPR: defineMutator(archiveImplementationSchema, async () => {}),
    /** Server-only — merges the GitHub PR. See {@link serverMutators}. */
    mergePR: defineMutator(prOperationSchema, async () => {}),
    /** Server-only — closes the GitHub PR. See {@link serverMutators}. */
    closePR: defineMutator(prOperationSchema, async () => {}),
    /** Server-only — reopens the GitHub PR. See {@link serverMutators}. */
    reopenPR: defineMutator(prOperationSchema, async () => {}),
    /** Server-only — sends a workflow event. */
    sendEvent: defineMutator(sendEventSchema, async () => {}),
    /**
     * Server-only — resolves a harness-readiness `action_required` suspension
     * for the current step visit, optionally recording an audited
     * current-visit model override. See {@link serverMutators}.
     */
    resolveStepRemediation: defineMutator(
      resolveStepRemediationSchema,
      async () => {},
    ),
  },
  preview: {
    /**
     * Server-only — reads sandbox config and creates a preview session.
     * See {@link serverMutators}.
     */
    startPreview: defineMutator(
      z.object({
        workspaceId: z.string().min(1),
        implementationId: z.string().min(1).optional(),
        runId: z.string().min(1).optional(),
        targetName: z.string().optional(),
      }),
      async () => {},
    ),
    /** Server-only — revokes a preview session. See {@link serverMutators}. */
    stopPreview: defineMutator(
      z.object({
        workspaceId: z.string().min(1),
        sessionId: z.string().min(1),
      }),
      async () => {},
    ),
  },
  github: {
    /**
     * Server-only — revokes the installation via the GitHub API before
     * cleaning up the DB row. See {@link serverMutators}.
     */
    removeInstallation: defineMutator(
      removeGithubInstallationSchema,
      async () => {},
    ),
    /** Server-only — refreshes accessible GitHub repositories into Zero. */
    refreshRepositories: defineMutator(
      refreshGithubRepositoriesSchema,
      async () => {},
    ),
  },
  automations: {
    create: defineMutator(createAutomationSchema, async ({ tx, ctx, args }) => {
      const access = await assertActiveWorkspaceMember(
        tx,
        ctx,
        args.workspaceId,
      )
      const now = new Date()

      const [primary] = args.repositories
      if (!primary) {
        throw new ApplicationError('At least one repository is required.')
      }
      const friction = args.triggerType === 'friction'

      await tx.mutate.automations.insert({
        id: args.id,
        workspaceId: access.workspaceId,
        userId: access.userId,
        name: args.name,
        description: args.description ?? null,
        instructions: args.instructions,
        repositoryName: primary.repositoryName,
        installationId: primary.installationId,
        baseBranch: primary.baseBranch ?? null,
        blueprintId: args.blueprintId,
        enabled: true,
        env: args.env ?? null,
        secretEnv: args.secretEnv ?? null,
        triggerType: args.triggerType,
        frictionMinOccurrences: friction
          ? (args.frictionMinOccurrences ?? null)
          : null,
      })
      await replaceAutomationRepositories(tx, args.id, args.repositories)

      // Friction automations run on reports, never on a schedule.
      for (const s of friction ? [] : args.schedules) {
        await tx.mutate.automationSchedules.insert({
          id: createId(),
          automationId: args.id,
          type: s.type,
          hour: s.hour ?? null,
          minute: s.minute ?? null,
          dayOfMonth: s.dayOfMonth ?? null,
          dayOfWeek: s.dayOfWeek ?? null,
          timezone: s.timezone,
          nextRunAt: computeNextRunAt(s, now).getTime(),
        })
      }
    }),
    update: defineMutator(updateAutomationSchema, async ({ tx, ctx, args }) => {
      await assertCanAccessAutomation(tx, ctx, args.id)

      const { id, repositories, ...rest } = args
      const [primary] = repositories ?? []
      await tx.mutate.automations.update({
        id,
        ...(primary
          ? {
              repositoryName: primary.repositoryName,
              installationId: primary.installationId,
              baseBranch: primary.baseBranch ?? null,
            }
          : {}),
        ...(rest.triggerType !== undefined
          ? { triggerType: rest.triggerType }
          : {}),
        ...(rest.frictionMinOccurrences !== undefined
          ? { frictionMinOccurrences: rest.frictionMinOccurrences }
          : {}),
        ...(rest.name !== undefined ? { name: rest.name } : {}),
        ...(rest.description !== undefined
          ? { description: rest.description }
          : {}),
        ...(rest.instructions !== undefined
          ? { instructions: rest.instructions }
          : {}),
        ...(rest.blueprintId !== undefined
          ? { blueprintId: rest.blueprintId }
          : {}),
        ...(rest.enabled !== undefined ? { enabled: rest.enabled } : {}),
        ...(rest.env !== undefined ? { env: rest.env } : {}),
        ...(rest.secretEnv !== undefined ? { secretEnv: rest.secretEnv } : {}),
      })
      if (repositories) {
        await replaceAutomationRepositories(tx, id, repositories)
      }
    }),
    delete: defineMutator(deleteAutomationSchema, async ({ tx, ctx, args }) => {
      await assertCanAccessAutomation(tx, ctx, args.id)

      if (tx.location === 'server') {
        await ctx?.automations?.cancelActiveRuns(args.id)

        const scheduleRows = Array.from(
          await tx.dbTransaction.query(
            'select id from automation_schedules where automation_id = $1',
            [args.id],
          ),
        )
        for (const row of scheduleRows) {
          const id = getString(row['id'])
          if (id !== null) {
            await tx.mutate.automationSchedules.delete({ id })
          }
        }
      }

      await tx.mutate.automations.delete({ id: args.id })
    }),
    toggleEnabled: defineMutator(
      toggleAutomationSchema,
      async ({ tx, ctx, args }) => {
        await assertCanAccessAutomation(tx, ctx, args.id)

        await tx.mutate.automations.update({
          id: args.id,
          enabled: args.enabled,
        })

        if (args.enabled && tx.location === 'server') {
          const now = new Date()
          const scheduleRows = Array.from(
            await tx.dbTransaction.query(
              'select id, type::text as type, hour, minute, day_of_month as "dayOfMonth", day_of_week as "dayOfWeek", timezone from automation_schedules where automation_id = $1',
              [args.id],
            ),
          )
          for (const row of scheduleRows) {
            const id = getString(row['id'])
            const type = getString(row['type'])
            if (id === null || type === null) continue
            const next = computeNextRunAt(
              {
                type,
                hour: row['hour'] as number | null,
                minute: row['minute'] as number | null,
                dayOfMonth: row['dayOfMonth'] as number | null,
                dayOfWeek: row['dayOfWeek'] as number | null,
                timezone: getString(row['timezone']) ?? 'UTC',
              },
              now,
            )
            await tx.mutate.automationSchedules.update({
              id,
              nextRunAt: next.getTime(),
            })
          }
        }
      },
    ),
    updateSchedules: defineMutator(
      updateSchedulesSchema,
      async ({ tx, ctx, args }) => {
        await assertCanAccessAutomation(tx, ctx, args.automationId)
        const now = new Date()

        if (tx.location === 'server') {
          const existing = Array.from(
            await tx.dbTransaction.query(
              'select id from automation_schedules where automation_id = $1',
              [args.automationId],
            ),
          )
          for (const row of existing) {
            const id = getString(row['id'])
            if (id !== null) {
              await tx.mutate.automationSchedules.delete({ id })
            }
          }
        }

        for (const s of args.schedules) {
          await tx.mutate.automationSchedules.insert({
            id: createId(),
            automationId: args.automationId,
            type: s.type,
            hour: s.hour ?? null,
            minute: s.minute ?? null,
            dayOfMonth: s.dayOfMonth ?? null,
            dayOfWeek: s.dayOfWeek ?? null,
            timezone: s.timezone,
            nextRunAt: computeNextRunAt(s, now).getTime(),
          })
        }
      },
    ),
    triggerManually: defineMutator(
      triggerAutomationSchema,
      async ({ tx, ctx, args }) => {
        await assertCanAccessAutomation(tx, ctx, args.id)

        if (tx.location !== 'server') {
          return
        }

        if (!ctx?.automations) {
          throw new ApplicationError('Automation runtime is not configured.')
        }

        const triggerRows = Array.from(
          await tx.dbTransaction.query(
            'select trigger_type::text as trigger_type from automations where id = $1',
            [args.id],
          ),
        )
        if (getString(triggerRows[0]?.['trigger_type']) === 'friction') {
          throw new ApplicationError(
            'Friction automations run when a friction item reaches their report count.',
          )
        }

        await ctx.automations.enqueueRun({
          automationId: args.id,
          scheduleId: 'manual',
        })
      },
    ),
  },
  feedback: {
    submit: defineMutator(submitFeedbackSchema, async ({ tx, ctx, args }) => {
      if (tx.location !== 'server') {
        return
      }

      const signedIn = assertSignedIn(ctx)
      const sideEffects = ctx?.feedback
      if (!sideEffects) {
        throw new ApplicationError('Feedback service is not configured.')
      }

      await sideEffects.submit({
        type: args.type,
        comment: args.comment,
        url: args.url,
        workspaceSlug: args.workspaceSlug,
        userAgent: args.userAgent,
        viewport: args.viewport,
        userId: signedIn.user.id,
        userName: signedIn.user.name,
        userEmail: signedIn.user.email,
      })
    }),
  },
  users: {
    updateProfile: defineMutator(
      updateUserProfileSchema,
      async ({ tx, ctx, args }) => {
        const signedIn = assertSignedIn(ctx)

        await tx.mutate.users.update({
          id: signedIn.user.id,
          ...(args.name !== undefined ? { name: args.name } : {}),
          ...(args.image !== undefined ? { image: args.image } : {}),
        })
      },
    ),
  },
  friction: {
    /**
     * Optimistic status change; the server override enforces membership, the
     * feature flag and the one-active-item rule. See {@link serverMutators}.
     */
    setStatus: defineMutator(
      setFrictionStatusSchema,
      async ({ tx, ctx, args }) => {
        await assertActiveWorkspaceMember(tx, ctx, args.workspaceId)
        await tx.mutate.frictionItems.update({
          id: args.id,
          status: args.status,
          dismissedReason:
            args.status === 'dismissed' ? (args.reason ?? null) : null,
        })
      },
    ),
    /**
     * Server-only — starts a fix task and marks the item `fixing`. See
     * {@link serverMutators}.
     */
    fix: defineMutator(fixFrictionSchema, async () => {}),
    /**
     * Server-only — creates the item's issue in the workspace's tracker. See
     * {@link serverMutators}.
     */
    createIssue: defineMutator(createFrictionIssueSchema, async () => {}),
  },
  briefs: {
    create: defineMutator(createBriefSchema, async ({ tx, ctx, args }) => {
      const access = await assertActiveWorkspaceMember(
        tx,
        ctx,
        args.workspaceId,
      )

      await tx.mutate.briefs.insert({
        id: args.id,
        workspaceId: access.workspaceId,
        title: args.title?.trim() || 'New brief',
        content: args.content ?? '',
        status: 'draft',
      })
    }),
    update: defineMutator(updateBriefSchema, async ({ tx, ctx, args }) => {
      await assertCanAccessBrief(tx, ctx, args.id)

      await tx.mutate.briefs.update({
        id: args.id,
        ...(args.title !== undefined ? { title: args.title } : {}),
        ...(args.content !== undefined ? { content: args.content } : {}),
      })
    }),
    setStatus: defineMutator(
      setBriefStatusSchema,
      async ({ tx, ctx, args }) => {
        await assertCanAccessBrief(tx, ctx, args.id)

        await tx.mutate.briefs.update({
          id: args.id,
          status: args.status,
        })
      },
    ),
    delete: defineMutator(deleteBriefSchema, async ({ tx, ctx, args }) => {
      if (tx.location === 'client') {
        return
      }

      await assertCanAccessBrief(tx, ctx, args.id)

      const resourceRows = Array.from(
        await tx.dbTransaction.query(
          'select id from brief_resources where brief_id = $1',
          [args.id],
        ),
      )

      for (const row of resourceRows) {
        const id = getString(row['id'])
        if (id !== null) {
          await tx.mutate.briefResources.delete({ id })
        }
      }

      await tx.mutate.briefs.delete({ id: args.id })
    }),
  },
  briefResources: {
    set: defineMutator(setBriefResourcesSchema, async ({ tx, ctx, args }) => {
      if (tx.location === 'client') {
        return
      }

      await assertCanAccessBrief(tx, ctx, args.briefId)

      const existingRows = Array.from(
        await tx.dbTransaction.query(
          'select id from brief_resources where brief_id = $1',
          [args.briefId],
        ),
      )

      for (const row of existingRows) {
        const id = getString(row['id'])
        if (id !== null) {
          await tx.mutate.briefResources.delete({ id })
        }
      }

      for (const resource of args.resources) {
        await tx.mutate.briefResources.insert({
          id: createId(),
          briefId: args.briefId,
          type: resource.type,
          externalId: resource.externalId,
          metadata: resource.metadata,
        })
      }
    }),
  },
})
